/**
 * Delta Calculation Utility
 * Calculates differences between original and current settings
 * Returns only fields that have changed
 */

/**
 * Calculate delta: Extract only fields that changed between original and current
 * Uses deep comparison for nested objects and arrays
 * @param original - Original settings object
 * @param current - Current settings object with potential changes
 * @returns Object containing only the fields that changed
 */
export function calculateDelta(original: any, current: any): Record<string, any> {
  const delta: Record<string, any> = {};

  /**
   * Internal recursive comparison function
   * @param orig - Original value
   * @param curr - Current value
   * @param path - Dot-notation path for nested fields (e.g., "idleScreen.enabled")
   */
  function compare(orig: any, curr: any, path: string = ''): void {
    // Same value - no change
    if (orig === curr) {
      return;
    }

    // Array comparison
    if (Array.isArray(orig) && Array.isArray(curr)) {
      const originalJson = JSON.stringify(orig);
      const currentJson = JSON.stringify(curr);
      if (originalJson !== currentJson) {
        delta[path] = curr;
      }
    }
    // Object comparison (recursive)
    else if (
      typeof orig === 'object' &&
      typeof curr === 'object' &&
      orig !== null &&
      curr !== null
    ) {
      for (const key in curr) {
        const newPath = path ? `${path}.${key}` : key;
        compare(orig?.[key], curr[key], newPath);
      }
    }
    // Primitive comparison (strings, numbers, booleans)
    else {
      delta[path] = curr;
    }
  }

  compare(original, current);

  return delta;
}

/**
 * Check if two values are deeply equal
 * @param a - First value
 * @param b - Second value
 * @returns true if deeply equal, false otherwise
 */
export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (typeof a !== 'object' || a === null || b === null) {
    return a === b;
  }

  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }

  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => deepEqual(a[key], b[key]));
}

/**
 * Check if there are any changes between two objects
 * @param original - Original object
 * @param current - Current object
 * @returns true if any changes detected, false if identical
 */
export function hasChanges(original: any, current: any): boolean {
  const delta = calculateDelta(original, current);
  return Object.keys(delta).length > 0;
}
